import { useCallback, useMemo } from "react";
import type { Product } from "@b2bportal/purchase-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import { ActionLink, ChoiceButton, Slot } from "@hopper-b2b/ui";
import { Typography } from "@material-ui/core";
import clsx from "clsx";
import "./styles.scss";

export interface ITravelerSelectRowProps {
  className?: string;
  value: string;
  selectRowType: string;
  isSelected: boolean;
  onClickEdit: () => void;
  onClickRemove?: () => void;
  onClickSelect?: () => void;
  buttonClassName?: string;
  wrapButton?: boolean;
  disabled?: boolean;
  isMobile?: boolean;
  isMinor?: boolean;
  productType?: Product;
  travelerAge?: number;
}

export const TravelerSelectRow = ({
  className,
  value,
  selectRowType,
  isSelected,
  onClickEdit,
  onClickRemove,
  onClickSelect,
  buttonClassName,
  wrapButton,
  disabled,
  isMobile,
  isMinor,
  productType,
}: ITravelerSelectRowProps): JSX.Element => {
  const { t, language } = useI18nContext();

  const travelerTypeEdit = useMemo(() => {
    return selectRowType === "phoneNumber"
      ? t("phone")
      : selectRowType === "email"
      ? t("email")
      : selectRowType;
  }, [selectRowType, t]);

  const onClickCheckboxHandler = useCallback(
    (e: Event | MouseEvent) => {
      e.stopPropagation();
      onClickSelect();
    },
    [onClickSelect]
  );

  const onClickEditHandler = useCallback(
    (e: Event | MouseEvent | React.KeyboardEvent) => {
      e.stopPropagation();
      onClickEdit();
    },
    [onClickEdit]
  );

  const children = (
    <div
      className={clsx("traveler-select-row-container", {
        selected: isSelected,
      })}
    >
      <div className="traveler-select-row-description">
        <Typography variant="h6" className="name" data-dd-privacy="mask">
          {value}
        </Typography>
        <Typography variant="body1" className="type" data-dd-privacy="mask">
          {travelerTypeEdit}
        </Typography>
        {wrapButton && (
          <div className="traveler-select-row-edit-delete-buttons-container">
            <ActionLink
              className="edit-button"
              disabled={disabled}
              onClick={onClickEditHandler}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  onClickEditHandler(e);
                }
              }}
              label={t("editTraveler")}
              content={
                <Typography className="action-link-text">
                  {t("edit")}
                </Typography>
              }
            />
            {onClickRemove && (
              <>
                <Typography
                  className={clsx("action-link-text", "action-link-separator")}
                >
                  |
                </Typography>
                <ActionLink
                  className="remove-button"
                  disabled={disabled}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      onClickRemove();
                    }
                  }}
                  onClick={onClickRemove}
                  label={t("removeTraveler")}
                  content={
                    <Typography className="action-link-text">
                      {t("delete")}
                    </Typography>
                  }
                />
              </>
            )}
          </div>
        )}
      </div>
      <div className="traveler-select-row-buttons-section">
        {!wrapButton && !disabled && (
          <>
            <ActionLink
              className="edit-button"
              disabled={disabled}
              onClick={onClickEditHandler}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  onClickEditHandler(e);
                }
              }}
              label={t("editTraveler")}
              content={
                <Typography className="action-link-text">
                  {t("edit")}
                </Typography>
              }
            />
            {onClickRemove && (
              <>
                <Typography
                  className={clsx("action-link-text", "action-link-separator")}
                >
                  |
                </Typography>
                <ActionLink
                  className="remove-button"
                  disabled={disabled}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      onClickRemove();
                    }
                  }}
                  onClick={onClickRemove}
                  label={t("removeTraveler")}
                  content={
                    <Typography className="action-link-text">
                      {t("delete")}
                    </Typography>
                  }
                />
              </>
            )}
          </>
        )}
        {onClickSelect ? (
          <Slot
            id="passenger-select-button"
            onClick={(e) => onClickCheckboxHandler(e)}
            isChosen={isSelected}
            disabled={!!disabled}
            className={`${buttonClassName} ${language}`}
            value={value}
            isMinor={isMinor}
            productType={productType}
            component={
              <ChoiceButton
                onClick={(e) => onClickCheckboxHandler(e)}
                isChosen={isSelected}
                disabled={!!disabled}
                ariaLabel={`${t("choose")} ${value}`}
                message={t("choose")}
                className={`${buttonClassName} ${language}`}
              />
            }
          />
        ) : null}
      </div>
    </div>
  );

  return (
    <Slot
      id="traveler-select-row-button-wrap"
      onClickSelect={onClickSelect}
      className={className}
      disabled={disabled}
      isMobile={isMobile}
      isSelected={isSelected}
      component={
        <div
          className={clsx("traveler-select-row-root", className, {
            disabled,
            mobile: isMobile,
          })}
        >
          {children}
        </div>
      }
    >
      {children}
    </Slot>
  );
};
